import React, { useState, useEffect, useCallback } from 'react';
import { api, servicesService } from '../../services/api';
import {
  ServerIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  SearchIcon,
  FilterIcon,
  RefreshCwIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SettingsIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Service {
  id: number;
  name: string;
  extension: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    email: string;
    phone?: string;
    whmcs_client_id?: number;
  };
  server: {
    id: number;
    name: string;
    url: string;
  };
  plan: {
    id: number;
    name: string;
    price: number;
    whmcs_product_id?: number;
  };
  whmcs_service_id?: number;
  whmcs_status?: string;
  phone_id?: string;
  meta_api_key?: string;
  client_secret?: string;
}

const AdminServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serverFilter, setServerFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [metaConfig, setMetaConfig] = useState({
    phone_id: '',
    meta_api_key: '',
  });
  const [isUpdatingMeta, setIsUpdatingMeta] = useState(false);
  const [selectedServiceForMeta, setSelectedServiceForMeta] = useState<Service | null>(null);

  const limit = 10;

  useEffect(() => {
    // Ler parâmetros da URL para filtros iniciais apenas uma vez
    if (!initialLoadDone) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchParam = urlParams.get('search');

      if (searchParam) {
        setSearchTerm(searchParam);
      }
      setInitialLoadDone(true);
    }
  }, [initialLoadDone]);

  const fetchServices = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (serverFilter) params.append('server_id', serverFilter);
      if (userFilter) params.append('user_id', userFilter);

      const response = await api.get(`/admin/services?${params}`);
      const data = response.data?.data;

      setServices(data.services || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalServices(data.pagination?.total || 0);
    } catch (err: any) {
      setError('Erro ao carregar serviços');
      console.error('Services error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, serverFilter, userFilter]);

  useEffect(() => {
    // Só fazer fetch após o carregamento inicial estar completo
    if (initialLoadDone) {
      fetchServices();
    }
  }, [currentPage, searchTerm, statusFilter, serverFilter, userFilter, initialLoadDone, fetchServices]);


  const handleSuspendService = async (service: Service) => {
    if (!window.confirm(`Tem certeza que deseja suspender o serviço "${service.name}"? O ramal será bloqueado.`)) {
      return;
    }

    try {
      await servicesService.suspendService(service.id);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao suspender serviço');
    }
  };

  const handleReleaseService = async (service: Service) => {
    if (!window.confirm(`Tem certeza que deseja liberar o serviço "${service.name}"? O ramal será reativado.`)) {
      return;
    }

    try {
      await servicesService.releaseService(service.id);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao liberar serviço');
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este serviço?')) {
      return;
    }

    try {
      await api.delete(`/admin/services/${serviceId}`);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir serviço');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'suspended':
        return <AlertCircleIcon className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <RefreshCwIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      case 'pending':
        return 'Pendente';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('pt-BR');
  };

  const toggleServiceExpansion = (serviceId: number) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setServerFilter('');
    setUserFilter('');
    setCurrentPage(1);
  };

  const openMetaModal = (service: Service) => {
    setSelectedServiceForMeta(service);
    setMetaConfig({
      phone_id: service.phone_id || '',
      meta_api_key: service.meta_api_key || '',
    });
    setShowMetaModal(true);
  };

  const closeMetaModal = () => {
    setShowMetaModal(false);
    setSelectedServiceForMeta(null);
    setMetaConfig({
      phone_id: '',
      meta_api_key: '',
    });
  };

  const handleUpdateMetaApi = async () => {
    if (!selectedServiceForMeta) return;

    if (!metaConfig.meta_api_key.trim() && !metaConfig.phone_id.trim()) {
      setError('Pelo menos um campo deve ser preenchido para atualizar a Meta API');
      return;
    }

    try {
      setIsUpdatingMeta(true);
      setError('');

      const updateData: any = {};

      if (metaConfig.meta_api_key.trim()) {
        updateData.meta_api_key = metaConfig.meta_api_key.trim();
      }

      if (metaConfig.phone_id.trim()) {
        updateData.phone_id = metaConfig.phone_id.trim();
      }

      const response = await api.put(`/admin/services/${selectedServiceForMeta.id}`, updateData);

      if (response.data.success) {
        // Success
        fetchServices();
        closeMetaModal();

        // Show success toast
        import('react-hot-toast').then(({ default: toast }) => {
          toast.success('Dados da Meta API atualizados com sucesso! ✅');
        });
      } else {
        setError(response.data.message || 'Erro ao atualizar dados da Meta API');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar dados da Meta API');
    } finally {
      setIsUpdatingMeta(false);
    }
  };

  if (isLoading && services.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Serviços</h1>
          <p className="mt-1 text-sm text-gray-500">
            Visualize e gerencie todos os serviços de todos os usuários
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <FilterIcon className="h-4 w-4 mr-2" />
            Filtros
            {showFilters ? (
              <ChevronUpIcon className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 ml-2" />
            )}
          </button>
          <button
            onClick={fetchServices}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <RefreshCwIcon className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <SearchIcon className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nome, ramal, usuário..."
                  className="pl-10 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="suspended">Suspenso</option>
                <option value="pending">Pendente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Servidor
              </label>
              <input
                type="text"
                placeholder="ID do servidor"
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                value={serverFilter}
                onChange={(e) => setServerFilter(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuário
              </label>
              <input
                type="text"
                placeholder="ID do usuário"
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Services List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {services.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhum serviço encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Tente ajustar os filtros ou crie um novo serviço.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200">
              {services.map((service) => (
                <li key={service.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className="flex-shrink-0">
                          <ServerIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {service.name}
                            </p>
                            <div className="ml-2 flex items-center">
                              {getStatusIcon(service.status)}
                            </div>
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Ramal: {service.extension}</span>
                            <span>Servidor: {service.server.name}</span>
                            <span>Plano: {service.plan.name}</span>
                            <button
                              onClick={() => toggleServiceExpansion(service.id)}
                              className="flex items-center text-cyan-600 hover:text-cyan-800"
                            >
                              <UserIcon className="h-4 w-4 mr-1" />
                              {service.user.name}
                              {expandedService === service.id ? (
                                <ChevronUpIcon className="h-4 w-4 ml-1" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 ml-1" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            service.status
                          )}`}
                        >
                          {getStatusText(service.status)}
                        </span>
                        <div className="flex items-center space-x-2">
                          {service.status === 'suspended' ? (
                            <button
                              onClick={() => handleReleaseService(service)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              title="Liberar serviço"
                            >
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Liberar
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSuspendService(service)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                              title="Suspender serviço"
                            >
                              <AlertCircleIcon className="h-3 w-3 mr-1" />
                              Suspender
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-400 hover:text-red-600"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Service Details */}
                    {expandedService === service.id && (
                      <div className="mt-4 pl-10 border-l-2 border-gray-200">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            Detalhes do Serviço
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                Informações do Usuário
                              </h5>
                              <div className="space-y-1">
                                <div className="flex items-center text-sm">
                                  <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="font-medium">{service.user.name}</span>
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                  <MailIcon className="h-4 w-4 mr-2" />
                                  {service.user.email}
                                </div>
                                {service.user.phone && (
                                  <div className="flex items-center text-sm text-gray-600">
                                    <PhoneIcon className="h-4 w-4 mr-2" />
                                    {service.user.phone}
                                  </div>
                                )}
                                {service.user.whmcs_client_id && (
                                  <div className="text-sm text-gray-600">
                                    WHMCS Client ID: {service.user.whmcs_client_id}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                Configurações Técnicas
                              </h5>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div>Servidor: {service.server.url}</div>
                                <div>Plano: R$ {(() => {
                                  try {
                                    const price = service.plan?.price;
                                    if (price === null || price === undefined) return 'N/A';
                                    const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
                                    return isNaN(numPrice) ? 'N/A' : numPrice.toFixed(2);
                                  } catch (error) {
                                    console.warn('Erro ao formatar preço:', error);
                                    return 'N/A';
                                  }
                                })()}</div>
                                {service.whmcs_service_id && (
                                  <div>WHMCS Service ID: {service.whmcs_service_id}</div>
                                )}
                                {service.whmcs_status && (
                                  <div>WHMCS Status: {service.whmcs_status}</div>
                                )}
                                {service.phone_id && (
                                  <div>Phone ID: {service.phone_id}</div>
                                )}
                                {service.meta_api_key && (
                                  <div className="flex items-center justify-between">
                                    <span>Meta API: Configurada</span>
                                    <button
                                      onClick={() => openMetaModal(service)}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      title="Editar Meta API"
                                    >
                                      <SettingsIcon className="h-3 w-3 mr-1" />
                                      Editar
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Criado em: {formatDate(service.created_at)}</span>
                              <span>Atualizado em: {formatDate(service.updated_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando{' '}
                    <span className="font-medium">
                      {Math.min((currentPage - 1) * limit + 1, totalServices)}
                    </span>{' '}
                    a{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * limit, totalServices)}
                    </span>{' '}
                    de{' '}
                    <span className="font-medium">{totalServices}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronUpIcon className="h-4 w-4 rotate-90" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      {currentPage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronDownIcon className="h-4 w-4 rotate-90" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Meta API Edit Modal */}
        {showMetaModal && selectedServiceForMeta && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600 mb-1">⚙️ Editar Meta API</h3>
                    <p className="text-gray-600 text-sm">Alterar dados da integração WhatsApp</p>
                  </div>
                  <button
                    onClick={closeMetaModal}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-sm text-blue-800 mb-1">
                      <strong>Serviço:</strong> {selectedServiceForMeta.name}
                    </p>
                    <p className="text-sm text-blue-700">
                      Usuário: {selectedServiceForMeta.user.name}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 mt-3">
                    <p className="text-sm text-amber-800">
                      <strong>Nota:</strong> Preencha apenas os campos que deseja atualizar. Pelo menos um campo deve ser preenchido.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone ID do Meta Business
                    </label>
                    <input
                      type="text"
                      required
                      className="block w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                      value={metaConfig.phone_id}
                      onChange={(e) => setMetaConfig({ ...metaConfig, phone_id: e.target.value })}
                      placeholder="Ex: 123456789012345"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      ID do número de telefone configurado no Meta Business
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Token da Meta API
                    </label>
                    <input
                      type="password"
                      required
                      className="block w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                      value={metaConfig.meta_api_key}
                      onChange={(e) => setMetaConfig({ ...metaConfig, meta_api_key: e.target.value })}
                      placeholder="Token de acesso da Meta API"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Token permanente de acesso à API do WhatsApp Business
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 rounded-md bg-red-50 p-3">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeMetaModal}
                    disabled={isUpdatingMeta}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpdateMetaApi}
                    disabled={(!metaConfig.meta_api_key.trim() && !metaConfig.phone_id.trim()) || isUpdatingMeta}
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
                  >
                    {isUpdatingMeta ? 'Atualizando...' : 'Atualizar Meta API'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServicesPage;